import type {
  DocumentUpsertModel,
  HomeListingLicensesModel,
  HomeListingLicensesUpdateModel,
} from 'api/models';
import { dateValidationMMDDYY } from 'utils/validation';
import {
  MediaFieldValue,
  parseDateMMDDYYYY,
  toDisplayDate,
  toIso,
  toMediaValue,
} from '../licenses.shared';

/**
 * Which municipal program applies to the home. Derived from the generated envelope so the values stay
 * checked against the API rather than being restated as free-standing string literals.
 *
 * Michigan has no state-level rental registration — every requirement is municipal — so the server
 * resolves this from the home's city and the step renders one variation per value. The client never
 * chooses it, which is what stops a home from declaring itself exempt.
 */
export type MIProgram = NonNullable<HomeListingLicensesModel['mi']>['program'];

export const MI_PROGRAM = {
  DETROIT: 'DetroitCertificateOfCompliance',
  GRAND_RAPIDS: 'GrandRapidsRegistrationAndCertification',
  CITY_REGISTRATION: 'CityRegistration',
  NOT_REQUIRED_CONDITIONAL: 'NotRequiredConditional',
  NOT_REQUIRED: 'NotRequired',
} as const satisfies Record<string, MIProgram>;

export function getMIProgram(
  currentStepFormData?: HomeListingLicensesModel | null,
): MIProgram | undefined {
  return currentStepFormData?.mi?.program;
}

export function getMICity(
  currentStepFormData?: HomeListingLicensesModel | null,
): string | undefined {
  return currentStepFormData?.mi?.city || undefined;
}

/** Whether the program expects a registration number and a document. */
export function requiresCapture(program?: MIProgram): boolean {
  return (
    program === MI_PROGRAM.DETROIT ||
    program === MI_PROGRAM.GRAND_RAPIDS ||
    program === MI_PROGRAM.CITY_REGISTRATION
  );
}

/** Only Detroit's Certificate of Compliance records an issuance date. */
export function showsIssuanceDate(program?: MIProgram): boolean {
  return program === MI_PROGRAM.DETROIT;
}

/**
 * Detroit certificates and Grand Rapids certifications both carry an expiry we can check for
 * currency. A plain city registration often doesn't issue one, so there it stays optional.
 */
export function requiresExpirationDate(program?: MIProgram): boolean {
  return program === MI_PROGRAM.DETROIT || program === MI_PROGRAM.GRAND_RAPIDS;
}

/**
 * Form state for the Michigan Licenses step. Field names mirror `MILicensesUpdateModel`, so the
 * mapping below only has to add the market envelope and convert dates and the uploaded file.
 *
 * Declared as a `type` rather than an `interface` so it keeps the implicit index signature that
 * `StepFormLayout`'s `initialValues: Record<string, unknown>` requires.
 */
export type MILicensesFormValues = {
  registrationNumber?: string;
  issuanceDate?: string;
  expirationDate?: string;
  file?: MediaFieldValue;
};

/**
 * Format check for a date field that may legitimately be left blank.
 *
 * `dateValidationMMDDYY` reads `value.length` before testing for a value, so it throws on an empty
 * field. Every other caller in the app pairs it with `required`, and `composeValidators` stops at the
 * first error, so it never sees `undefined` there. A plain city registration often carries no expiry,
 * which makes this the one date field on the step that is optional — so it needs a validator that
 * passes on empty instead of crashing on it.
 */
export function optionalDateMMDDYYYY(value?: string): string | undefined {
  if (!value) return undefined;

  return dateValidationMMDDYY(value);
}

/**
 * An expiry that has already passed. Surfaced as a warning rather than a blocker: unlike DC, where an
 * unregistered home legally cannot be listed, a lapsed Michigan certificate with a renewal in flight
 * is an ordinary state and stopping the flow there costs more than it protects.
 */
export function isExpired(value?: string): boolean {
  const date = parseDateMMDDYYYY(value);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date < today;
}

/** Maps `GET …/unit/licenses/{stepId}` onto form state. */
export function toMILicensesFormValues(
  currentStepFormData?: HomeListingLicensesModel | null,
): MILicensesFormValues {
  const mi = currentStepFormData?.mi;
  if (!mi) return {};

  const values: MILicensesFormValues = {
    registrationNumber: mi.registrationNumber,
    issuanceDate: toDisplayDate(mi.issuanceDate),
    expirationDate: toDisplayDate(mi.expirationDate),
  };

  const file = toMediaValue(mi.file);
  if (file) values.file = file;

  return values;
}

/**
 * Builds the `PUT …/unit/licenses/{stepId}` body. The market envelope is required:
 * `HomeListingLicensesUpdateModel` carries nothing but `dc` and `mi`, so a flat payload is discarded
 * wholesale by the backend.
 *
 * The `HomeListingLicensesUpdateModel` return type is load-bearing — it is what keeps every field
 * name here checked against the generated client instead of failing silently at runtime.
 */
export function toMILicensesUpdatePayload(
  values: MILicensesFormValues,
  { program, file }: { program?: MIProgram; file?: DocumentUpsertModel },
): HomeListingLicensesUpdateModel {
  // The two "not required" programs capture nothing. The envelope still names the market so the
  // request states which payload it means, but the backend reads no fields from it.
  if (!requiresCapture(program)) return { mi: {} };

  return {
    mi: {
      registrationNumber: values.registrationNumber,
      // Dropped for the programs that don't ask for it, so a value left behind by a corrected city
      // isn't persisted against a program that has no issuance date.
      issuanceDate: showsIssuanceDate(program)
        ? toIso(values.issuanceDate)
        : undefined,
      expirationDate: toIso(values.expirationDate),
      file,
    },
  };
}
